import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp
} from 'firebase/firestore';
import { auth, db } from '../firebase';
import { scrollPageToTop, scrollElementIntoView } from '../utils/scrollPageToTop';
import { validateMessage } from '../utils/profanityFilter';
import { isInterestFresh, isListingStillAvailable } from '../utils/listingInterestPolicy';
import { getListedBalance, formatBalanceLabel, getBalanceBadgeClass } from '../utils/tradeFairness';
import { recordOfferIntentEvent, OFFER_INTENT_KINDS } from '../utils/offerIntentAnalytics';
import { placeholderFor, SKETCH_PLACEHOLDER_URL } from '../utils/listingPlaceholder';
import { offerModeOf } from '../utils/offerMode';
import AutoGrowTextarea from '../components/AutoGrowTextarea';

/**
 * Owner-side response to a "Hey, I'm interested" signal (beta).
 *
 * The sender named nothing, so this screen's job is to answer "is there
 * anything here worth trading for?" with as little work as possible: their
 * board, priced against the pedal they asked about, and three ways out —
 * including a graceful one.
 */
const savedPicksKey = (interestId) => `interestResponse_${interestId}`;

export function savePicks(interestId, { selectedIds, responseNote, scrollY }) {
  try {
    sessionStorage.setItem(savedPicksKey(interestId), JSON.stringify({ selectedIds, responseNote, scrollY }));
  } catch (e) { /* storage unavailable: the picks just aren't kept */ }
}

/** Read and clear. Null when nothing (valid) was saved. */
export function takeSavedPicks(interestId) {
  try {
    const raw = sessionStorage.getItem(savedPicksKey(interestId));
    sessionStorage.removeItem(savedPicksKey(interestId));
    if (!raw) return null;
    const v = JSON.parse(raw);
    return {
      selectedIds: Array.isArray(v.selectedIds) ? v.selectedIds.filter((id) => typeof id === 'string') : [],
      responseNote: typeof v.responseNote === 'string' ? v.responseNote : '',
      scrollY: Number.isFinite(v.scrollY) ? v.scrollY : 0,
    };
  } catch (e) {
    return null;
  }
}

const InterestResponse = () => {
  const { interestId } = useParams();
  const navigate = useNavigate();

  const [currentUser, setCurrentUser] = useState(null);
  const [authResolved, setAuthResolved] = useState(false);
  const [myListing, setMyListing] = useState(null);
  // Set when the pedal being discussed has sold, traded, or been switched off.
  // The server deletes the interest on that same event, so reaching this page in
  // that state means racing the trigger or holding a page open across it —
  // rare, and silent without something on screen to say so.
  const [listingGone, setListingGone] = useState(false);
  const [sender, setSender] = useState(null);
  const [senderPedals, setSenderPedals] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [responseNote, setResponseNote] = useState('');
  const [noteError, setNoteError] = useState('');
  const [doneMessage, setDoneMessage] = useState('');
  const [handledStatus, setHandledStatus] = useState('');
  const confirmPanelRef = useRef(null);
  // Picks, note and scroll position saved when the owner opens one of the
  // sender's listings ("View listing"), restored when they come back. Until
  // 2026-10-12 there was no way to look at a pedal from here, and leaving the
  // page any other way threw the picks away.
  const pendingRestoreRef = useRef(null);

  // The confirm step opens below a whole board of pedals, so on any real board
  // it appears off-screen and the tap looks like it did nothing. 'start' rather
  // than centred because the panel is tall — the line naming what's about to be
  // sent is the part that has to be visible.
  useEffect(() => {
    if (!confirming) return undefined;
    return scrollElementIntoView(() => confirmPanelRef.current, { block: 'start' });
  }, [confirming]);

  useEffect(() => {
    const unsub = auth.onAuthStateChanged((user) => {
      setCurrentUser(user);
      setAuthResolved(true);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!authResolved) return;
    if (!currentUser) {
      setError('Please sign in to view this.');
      setLoading(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      try {
        const interestSnap = await getDoc(doc(db, 'listingInterest', interestId));
        if (!interestSnap.exists()) {
          if (!cancelled) {
            setError('This interest signal is no longer available.');
            setLoading(false);
          }
          return;
        }
        const interestData = { id: interestSnap.id, ...interestSnap.data() };
        if (interestData.listingOwnerId !== currentUser.uid) {
          if (!cancelled) {
            setError('This page belongs to another member.');
            setLoading(false);
          }
          return;
        }
        // A signal past its life is gone as far as either side is concerned,
        // even if the nightly cleanup hasn't reached it yet.
        if (!isInterestFresh(interestData.createdAt)) {
          if (!cancelled) {
            setError('This interest signal has expired.');
            setLoading(false);
          }
          return;
        }
        // Only an untouched signal gets the response screen. Reached from an old
        // email or an unread notification, this page would otherwise let the
        // owner answer a second time, quietly replacing the picks the sender was
        // already shown and firing no notification about the change.
        if (interestData.status !== 'open') {
          if (!cancelled) {
            setHandledStatus(interestData.status);
            setLoading(false);
          }
          return;
        }

        const [listingSnap, profileSnap, pedalsSnap] = await Promise.all([
          getDoc(doc(db, 'listings', interestData.listingId)),
          getDoc(doc(db, 'publicProfiles', interestData.fromUserId)),
          getDocs(
            query(
              collection(db, 'listings'),
              where('ownerId', '==', interestData.fromUserId),
              where('active', '==', true)
            )
          )
        ]);

        const pedals = [];
        pedalsSnap.forEach((d) => {
          const data = d.data();
          if (!data.inTrade && !data.needsReactivation && offerModeOf(data) !== 'sell') pedals.push({ id: d.id, ...data });
        });

        if (cancelled) return;
        // The snapshot fallback is a cached header — make/model/photo frozen at
        // the moment the interest was sent — so a deleted listing still renders
        // something to name the conversation. It cannot speak to availability,
        // which is why a missing listing counts as gone rather than unknown.
        const liveListing = listingSnap.exists()
          ? { id: listingSnap.id, ...listingSnap.data() }
          : null;
        setMyListing(liveListing || interestData.listingSnapshot || null);
        setListingGone(!isListingStillAvailable(liveListing));
        setSender(profileSnap.exists() ? { id: profileSnap.id, ...profileSnap.data() } : null);
        setSenderPedals(pedals);
        const restored = takeSavedPicks(interestId);
        if (restored) {
          // Only pedals still on the board; one that sold meanwhile drops out.
          const onBoard = new Set(pedals.map((p) => p.id));
          setSelectedIds(restored.selectedIds.filter((id) => onBoard.has(id)));
          setResponseNote(restored.responseNote);
          pendingRestoreRef.current = restored.scrollY;
        }
        setLoading(false);
        void recordOfferIntentEvent(OFFER_INTENT_KINDS.INTEREST_OPENED, { interestId });
      } catch (err) {
        console.error('❌ Failed to load interest:', err);
        if (!cancelled) {
          setError('Something went wrong loading this. Please try again.');
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [authResolved, currentUser, interestId]);

  // Put the owner back where they were on the board once it has rendered.
  useEffect(() => {
    if (loading || pendingRestoreRef.current == null) return undefined;
    const y = pendingRestoreRef.current;
    pendingRestoreRef.current = null;
    const timer = setTimeout(() => window.scrollTo(0, y), 0);
    return () => clearTimeout(timer);
  }, [loading]);

  const savePicksForReturn = () => {
    savePicks(interestId, { selectedIds, responseNote, scrollY: window.scrollY || 0 });
  };

  const toggleSelection = (pedalId) => {
    // Nothing to trade for once the pedal is gone. Guarded here as well as on
    // the card so a selection can't survive the listing going unavailable while
    // this page is open.
    if (listingGone) return;
    // Changing the selection invalidates whatever the confirm step was about to
    // send, so drop back to the buttons rather than confirm a stale list.
    setConfirming(false);
    setSelectedIds((prev) =>
      prev.includes(pedalId) ? prev.filter((id) => id !== pedalId) : [...prev, pedalId]
    );
  };

  const markStatus = async (status, extra = {}) => {
    await updateDoc(doc(db, 'listingInterest', interestId), {
      status,
      respondedAt: serverTimestamp(),
      ...extra
    });
  };

  /**
   * Record the reply on the original interest rather than as one new interest
   * per pedal. A reply is a single event in one conversation: writing it as N
   * documents fanned out N notifications and N emails, and left the sender with
   * N cards to piece back together.
   */
  const handleInterestedInThese = async () => {
    if (!selectedIds.length || submitting) return;
    // The last line of defence. A reply here would promise a trade for a pedal
    // the owner no longer has, and the sender would be told an offer is worth
    // building. Passing stays open — that ends the conversation honestly.
    if (listingGone) return;

    const trimmedNote = responseNote.trim().slice(0, 500);
    if (trimmedNote) {
      const validation = validateMessage(trimmedNote);
      if (!validation.isValid) {
        setNoteError(validation.error);
        return;
      }
    }
    setNoteError('');

    setSubmitting(true);
    try {
      // Fires notifyListingInterestResponse: one notification and one email,
      // naming every pedal picked.
      await markStatus('actioned', {
        responsePedalIds: selectedIds,
        responseNote: trimmedNote || null
      });
      setDoneMessage(
        selectedIds.length === 1
          ? `${sender?.username || 'They'} will see that you're interested in this pedal.`
          : `${sender?.username || 'They'} will see that you're interested in these ${selectedIds.length} of their pedals.`
      );
      // The confirmation replaces a full page of pedals, so without this the
      // user lands somewhere in the middle of an otherwise empty screen.
      scrollPageToTop();
    } catch (err) {
      console.error('❌ Reciprocal interest failed:', err);
      setError('Could not send that. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePass = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await markStatus('dismissed');
      void recordOfferIntentEvent(OFFER_INTENT_KINDS.INTEREST_DISMISSED, { interestId });
      // The pass button sits at the bottom of a board's worth of pedals, and the
      // browser carries that offset into the next route, so the Trade Center
      // opens somewhere in its middle. The retries here outlast the new page's
      // first paint.
      scrollPageToTop();
      // Land at the TOP of Offers (the interest just left the list), keeping
      // the tab: opts out of the Offers page's position restore.
      navigate('/trade-center', { state: { skipViewRestore: true } });
    } catch (err) {
      console.error('❌ Could not dismiss interest:', err);
      setError('Could not update this. Please try again.');
      setSubmitting(false);
    }
  };

  const pageStyle = {
    backgroundColor: '#F7F3EB',
    minHeight: '100vh',
    padding: '2rem 1rem',
    fontFamily: 'Nunito, sans-serif'
  };

  if (loading) {
    return (
      <div style={pageStyle}>
        <p style={{ textAlign: 'center', color: '#6b7280' }}>Loading…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={pageStyle}>
        <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
          <p style={{ color: '#6b7280', marginBottom: '1rem' }}>{error}</p>
          <Link to="/trade-center" style={{ color: '#5A9ECB' }}>Back to Trade Center</Link>
        </div>
      </div>
    );
  }

  if (handledStatus) {
    const handledCopy = {
      actioned: "You've already replied to this one. They'll come back with an offer if it works for them.",
      dismissed: "You've already passed on this one.",
      offered: 'This one turned into an offer. It\'s waiting in your Trade Center.',
      closed: 'This one has been closed out.'
    }[handledStatus] || 'This one has already been handled.';
    return (
      <div style={pageStyle}>
        <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
          <p style={{ color: '#6b7280', marginBottom: '1rem' }}>{handledCopy}</p>
          <Link to="/trade-center" style={{ color: '#5A9ECB' }}>Back to Trade Center</Link>
        </div>
      </div>
    );
  }

  if (doneMessage) {
    return (
      <div style={pageStyle}>
        <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ color: '#374151', marginBottom: '0.5rem' }}>Sent!</h2>
          <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>{doneMessage}</p>
          <Link
            to="/trade-center"
            style={{
              display: 'inline-block', backgroundColor: '#C9A227', color: 'white',
              padding: '12px 28px', borderRadius: '9999px', fontWeight: '700',
              textDecoration: 'none'
            }}
          >
            Back to Trade Center
          </Link>
        </div>
      </div>
    );
  }

  const senderName = sender?.username || 'A member';
  const myPedalName = [myListing?.make, myListing?.model].filter(Boolean).join(' ') || 'your pedal';
  const selectedPedals = senderPedals.filter((pedal) => selectedIds.includes(pedal.id));

  return (
    <div style={pageStyle}>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <h2 style={{ color: '#374151', fontSize: '1.4rem', margin: '0 0 6px' }}>
            {senderName} is interested in your {myPedalName}
          </h2>
          <p style={{ color: '#6b7280', fontSize: '0.95rem', margin: 0 }}>
            Let them know which pedals you might be interested in trading for.
          </p>
        </div>

        {myListing && (
          <div style={{
            backgroundColor: 'white', borderRadius: '12px', padding: '1rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: '1.5rem',
            display: 'flex', alignItems: 'center', gap: '1rem'
          }}>
            <img
              src={myListing.images?.top || myListing.imageUrl || placeholderFor(myListing).url}
              alt={myPedalName}
              style={{ width: '72px', height: '72px', objectFit: 'cover', borderRadius: '8px' }}
            />
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontSize: '0.7rem', color: '#9ca3af', fontWeight: '700', letterSpacing: '0.05em' }}>
                THEY WANT
              </div>
              <div style={{ fontWeight: '700', color: '#374151' }}>{myPedalName}</div>
              {myListing.condition && (
                <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                  {myListing.condition}
                  {myListing.price ? ` · $${myListing.price}` : ''}
                </div>
              )}
              {listingGone && (
                <div style={{
                  fontSize: '0.85rem', color: '#b91c1c', fontWeight: '700', marginTop: '4px'
                }}>
                  This pedal is no longer available, it&apos;s been sold, traded, or switched off.
                </div>
              )}
            </div>
          </div>
        )}

        <h3 style={{ color: '#374151', fontSize: '1.1rem', marginBottom: '4px' }}>
          {senderName}&apos;s pedalboard
        </h3>
        <p style={{ color: '#6b7280', fontSize: '0.9rem', marginTop: 0, marginBottom: '1rem' }}>
          Select anything you&apos;d consider
          {selectedIds.length > 0 && (
            <span style={{ fontWeight: '700', color: '#5A9ECB' }}> ({selectedIds.length} selected)</span>
          )}
        </p>

        {senderPedals.length === 0 ? (
          <div style={{
            backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem',
            textAlign: 'center', color: '#6b7280', marginBottom: '1.5rem'
          }}>
            {senderName} doesn't have any pedals available to trade right now.
            {listingGone
              ? ' There\'s nothing to reply with either way, your pedal is no longer available.'
              : " You can still let them know you're open to something, or pass."}
          </div>
        ) : (
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
            gap: '1rem', marginBottom: '1.5rem'
          }}>
            {senderPedals.map((pedal) => {
              const isSelected = selectedIds.includes(pedal.id);
              const selectionIndex = selectedIds.indexOf(pedal.id);
              // Owner gives up their listing, receives this pedal.
              const balance = getListedBalance([myListing?.price], pedal.price);
              return (
                <div
                  key={pedal.id}
                  onClick={() => toggleSelection(pedal.id)}
                  style={{
                    position: 'relative',
                    cursor: listingGone ? 'default' : 'pointer',
                    // Dimmed rather than hidden: the board is the context for
                    // the warning above it, and pulling it would leave the
                    // owner staring at an empty page with no explanation.
                    opacity: listingGone ? 0.5 : 1,
                    backgroundColor: isSelected ? '#EFF6FF' : 'white',
                    borderRadius: '12px', padding: '12px', textAlign: 'center',
                    boxShadow: isSelected
                      ? '0 4px 12px rgba(0, 0, 0, 0.15), 0 0 0 3px rgba(59, 130, 246, 0.4)'
                      : '0 1px 3px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.2s ease-in-out'
                  }}
                  onMouseEnter={(e) => { if (!isSelected && !listingGone) e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.12)'; }}
                  onMouseLeave={(e) => { if (!isSelected && !listingGone) e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)'; }}
                >
                  {isSelected && (
                    <div style={{
                      position: 'absolute', top: '6px', left: '6px', zIndex: 5,
                      backgroundColor: '#3b82f6', color: 'white',
                      borderRadius: '50%', width: '24px', height: '24px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.75rem', fontWeight: 'bold',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}>
                      {selectionIndex + 1}
                    </div>
                  )}
                  {/* Wrapper exists so the balance ribbon can pin to the bottom
                      of the image instead of the bottom of the card. */}
                  <div style={{ position: 'relative', marginBottom: '8px' }}>
                    <img
                      src={pedal.images?.top || placeholderFor(pedal).url}
                      alt={`${pedal.make} ${pedal.model}`}
                      style={{
                        width: '100%', aspectRatio: '1', objectFit: 'cover',
                        borderRadius: '10px', display: 'block'
                      }}
                    />
                    {balance && (
                      <div className={getBalanceBadgeClass(balance)}>
                        {formatBalanceLabel(balance)}
                      </div>
                    )}
                  </div>
                  <div style={{ fontWeight: '700', color: '#374151', fontSize: '0.9rem', lineHeight: '1.2' }}>
                    {pedal.make}
                  </div>
                  <div style={{ color: '#6b7280', fontSize: '0.85rem', lineHeight: '1.2' }}>
                    {pedal.model}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: '4px' }}>
                    {pedal.condition}{pedal.price ? ` · $${pedal.price}` : ''}
                  </div>
                  {/* stopPropagation: the card itself toggles the pick. */}
                  <Link
                    to={`/listing/${pedal.id}`}
                    onClick={(e) => { e.stopPropagation(); savePicksForReturn(); }}
                    className='hover:underline'
                    style={{ color: '#5A9ECB', fontSize: '0.8rem', marginTop: '6px', display: 'inline-block', fontWeight: 600 }}
                  >
                    View listing
                  </Link>
                </div>
              );
            })}
          </div>
        )}

        {confirming ? (
          // This sends the moment it's confirmed, so it gets a look first — and
          // it's the only place the owner can say anything, since the offer
          // itself comes back from the other side.
          <div
            ref={confirmPanelRef}
            style={{
              backgroundColor: 'white', borderRadius: '12px', padding: '1rem 1.25rem',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)', textAlign: 'left'
            }}
          >
            <div style={{ fontWeight: '700', color: '#374151', marginBottom: '6px' }}>
              Let {senderName} know you're interested in {selectedIds.length === 1 ? 'this pedal' : `these ${selectedIds.length} pedals`}?
            </div>
            <ul style={{ margin: '0 0 10px', paddingLeft: '1.1rem', color: '#4b5563', fontSize: '0.9rem' }}>
              {selectedPedals.map((pedal) => (
                <li key={pedal.id}>
                  {[pedal.make, pedal.model].filter(Boolean).join(' ')}
                  {pedal.price ? ` · $${pedal.price}` : ''}
                </li>
              ))}
            </ul>
            <p style={{ color: '#6b7280', fontSize: '0.9rem', margin: '0 0 12px' }}>
              We&apos;ll tell them which ones caught your eye, and see if they come back with an offer.
            </p>
            {/* They build the offer, so terms are theirs to set. This is where
                the owner can steer it — "I'd want cash on top" — instead of
                waiting for an offer that misses what they wanted. */}
            <label
              htmlFor="interest-response-note"
              style={{ display: 'block', color: '#374151', fontSize: '0.85rem', fontWeight: '600', marginBottom: '4px' }}
            >
              Anything to add? (optional)
            </label>
            <AutoGrowTextarea
              id="interest-response-note"
              value={responseNote}
              onChange={(e) => { setResponseNote(e.target.value); setNoteError(''); }}
              maxLength={500}
              rows={3}
              placeholder="e.g. I'd want some cash on top, or I'm most interested in the Bigger Bloom"
              style={{
                width: '100%', boxSizing: 'border-box', padding: '10px 12px',
                border: `1px solid ${noteError ? '#dc2626' : '#d1d5db'}`, borderRadius: '10px',
                fontFamily: 'Nunito, sans-serif', color: '#374151',
                marginBottom: noteError ? '4px' : '12px'
              }}
            />
            {noteError && (
              <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0 0 12px' }}>{noteError}</p>
            )}
            <div className="interest-cta-row" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              <button
                type="button"
                onClick={handleInterestedInThese}
                disabled={submitting || listingGone}
                style={{
                  backgroundColor: submitting || listingGone ? '#9CA3AF' : '#C9A227',
                  color: 'white', border: 'none', borderRadius: '9999px',
                  padding: '14px 30px', fontWeight: '700', fontSize: '1rem',
                  fontFamily: 'Nunito, sans-serif',
                  cursor: submitting || listingGone ? 'not-allowed' : 'pointer'
                }}
              >
                {submitting ? 'Sending…' : 'Send it'}
              </button>
              <button
                type="button"
                onClick={() => setConfirming(false)}
                disabled={submitting}
                style={{
                  backgroundColor: '#E5E7EB', color: '#374151',
                  border: 'none', borderRadius: '9999px',
                  padding: '14px 30px', fontWeight: '400', fontSize: '1rem',
                  fontFamily: 'Nunito, sans-serif',
                  cursor: submitting ? 'not-allowed' : 'pointer'
                }}
              >
                Go back
              </button>
            </div>
          </div>
        ) : (
          <div>
            {/* The warning on the header card is a full board away by the time
                anyone reaches these buttons, so the reason the reply is greyed
                out has to be repeated where the greying is. */}
            {listingGone && (
              <p className="interest-cta-note" style={{
                color: '#b91c1c', fontSize: '0.9rem', fontWeight: '700',
                textAlign: 'center', margin: '0 0 10px'
              }}>
                You can&apos;t reply, your {myPedalName} is no longer available. You can still pass.
              </p>
            )}
            <div className="interest-cta-row" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'center' }}>
              <button
                type="button"
                onClick={() => setConfirming(true)}
                disabled={!selectedIds.length || submitting || listingGone}
                style={{
                  backgroundColor: !selectedIds.length || submitting || listingGone ? '#9CA3AF' : '#C9A227',
                  color: 'white', border: 'none', borderRadius: '9999px',
                  padding: '14px 30px', fontWeight: '700', fontSize: '1rem',
                  fontFamily: 'Nunito, sans-serif',
                  cursor: !selectedIds.length || submitting || listingGone ? 'not-allowed' : 'pointer'
                }}
              >
                I'm interested in these
              </button>
              <button
                type="button"
                onClick={handlePass}
                disabled={submitting}
                style={{
                  backgroundColor: '#E5E7EB', color: '#374151',
                  border: 'none', borderRadius: '9999px',
                  padding: '14px 30px', fontWeight: '400', fontSize: '1rem',
                  fontFamily: 'Nunito, sans-serif',
                  cursor: submitting ? 'not-allowed' : 'pointer'
                }}
              >
                Not for me, thanks
              </button>
            </div>
          </div>
        )}

        <style>{`
          /* Mobile stacks these full-bleed, so centered text left the labels
             wandering relative to the copy above them and to the listing
             page's interest button. */
          @media (max-width: 768px) {
            /* Side by side rather than stacked. At full label width the pair
               overflows an iPhone-class viewport and wraps, so the type and
               padding come down enough to fit instead. */
            .interest-cta-row {
              justify-content: flex-start !important;
              flex-wrap: nowrap !important;
            }
            /* Split the row into equal halves. Type scales with the viewport
               so the longer label stays on one line at any phone width
               instead of wrapping into a two-line slab, and it stops growing
               once it reaches the desktop size.
               !important because these buttons carry inline padding and
               font-size, which otherwise beat any stylesheet rule. */
            .interest-cta-row > button {
              flex: 1 1 0;
              min-width: 0;
              padding: 13px 8px !important;
              font-size: clamp(0.8rem, 3.4vw, 1rem) !important;
              white-space: nowrap;
            }
            .interest-cta-note {
              text-align: left !important;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default InterestResponse;
